/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import type { ReactNode } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import { DateField } from '@/shared/ui/components/form/inputs';
import { MaskedIntegerField, MaskedNumberField } from './MaskedNumberField';
import { AUCTION_TRANSACTION, PROPERTY_FIELD_LIMITS, SHORT_TERM_TRANSACTION } from './schema';
import type { PropertyFormValues } from './schema';

interface TransactionPricingBlocksProps {
  form: UseFormReturn<PropertyFormValues>;
}

type MoneyKey = 'short_term_price' | 'short_term_cleaning_fee' | 'auction_first_price' | 'auction_second_price';
type DateKey = 'auction_first_date' | 'auction_second_date';

/**
 * A dashed block that appears only while its transaction is selected.
 *
 * @param props - Title, the hint saying why it is shown, and its fields.
 * @returns The block.
 */
function ConditionalBlock({ title, hint, testId, children }: { title: string; hint: string; testId: string; children: ReactNode }) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col gap-(--space-3) rounded-(--radius-md) border border-dashed border-[var(--border-medium)] p-(--space-3)"
    >
      <div className="flex flex-col gap-(--space-0-5)">
        <Text variant="small" as="span" className="[font-weight:var(--font-semibold)] text-[var(--text-secondary)]">
          {title}
        </Text>
        <Text variant="meta" as="span" className="text-[var(--text-tertiary)]">
          {hint}
        </Text>
      </div>
      {children}
    </div>
  );
}

/**
 * A currency field bound to the form.
 *
 * @param props - Form, field name and label.
 * @returns The field.
 */
function MoneyField({ form, name, label }: { form: UseFormReturn<PropertyFormValues>; name: MoneyKey; label: string }) {
  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field }) => (
        <MaskedNumberField
          label={label}
          suffix="R$"
          error={form.formState.errors[name]?.message}
          value={field.value}
          onChange={field.onChange}
          maxDigits={PROPERTY_FIELD_LIMITS.money.maxDigits}
        />
      )}
    />
  );
}

/**
 * A date field bound to the form, holding the ISO date.
 *
 * @param props - Form, field name and label.
 * @returns The field.
 */
function IsoDateField({ form, name, label }: { form: UseFormReturn<PropertyFormValues>; name: DateKey; label: string }) {
  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field }) => (
        <DateField label={label} value={field.value ?? ''} onChange={field.onChange} />
      )}
    />
  );
}

/**
 * The Temporada and Leilão price blocks, each shown while its transaction is
 * selected. Typing a price there keeps the chip on (the form marks it).
 *
 * @param props - The property drawer form.
 * @returns The blocks for the selected transactions.
 */
export function TransactionPricingBlocks({ form }: TransactionPricingBlocksProps) {
  const { t } = useTranslation('features/properties');
  const types = form.watch('transaction_type') ?? [];
  const showShortTerm = types.includes(SHORT_TERM_TRANSACTION);
  const showAuction = types.includes(AUCTION_TRANSACTION);

  return (
    <>
      {showShortTerm && (
        <ConditionalBlock
          testId="short-term-block"
          title={t('propertyDrawer.shortTerm.title')}
          hint={t('propertyDrawer.shortTerm.hint')}
        >
          <MoneyField form={form} name="short_term_price" label={t('propertyDrawer.shortTerm.price')} />
          <div className="grid grid-cols-2 gap-(--space-3)">
            <Controller
              name="short_term_guests"
              control={form.control}
              render={({ field }) => (
                <MaskedIntegerField
                  label={t('propertyDrawer.shortTerm.guests')}
                  maxDigits={3}
                  error={form.formState.errors.short_term_guests?.message}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <MoneyField form={form} name="short_term_cleaning_fee" label={t('propertyDrawer.shortTerm.cleaningFee')} />
          </div>
        </ConditionalBlock>
      )}
      {showAuction && (
        <ConditionalBlock
          testId="auction-block"
          title={t('propertyDrawer.auction.title')}
          hint={t('propertyDrawer.auction.hint')}
        >
          <div className="grid grid-cols-2 gap-(--space-3)">
            <IsoDateField form={form} name="auction_first_date" label={t('propertyDrawer.auction.firstDate')} />
            <MoneyField form={form} name="auction_first_price" label={t('propertyDrawer.auction.firstPrice')} />
            <IsoDateField form={form} name="auction_second_date" label={t('propertyDrawer.auction.secondDate')} />
            <MoneyField form={form} name="auction_second_price" label={t('propertyDrawer.auction.secondPrice')} />
          </div>
        </ConditionalBlock>
      )}
    </>
  );
}
